import type { Message } from '../api/types.ts'

/**
 * НЕОТПРАВЛЕННОЕ
 *
 * Телефон теряет сеть постоянно: метро, лифт, подъезд, дача. Сообщение,
 * набранное в этот момент, не должно пропадать вместе с ошибкой сети —
 * в привычных мессенджерах оно висит с часиками и уходит само, когда
 * связь вернулась.
 *
 * Очередь переживает перезагрузку страницы: человек написал в метро,
 * закрыл браузер, поднялся наверх — сообщение уйдёт при следующем
 * открытии. Поэтому она лежит в хранилище браузера, а не в памяти.
 *
 * Порядок сохраняется: два сообщения подряд уйдут в том же порядке,
 * в каком их набрали. Иначе ответ приходит раньше вопроса.
 */

const KEY = 'family.outbox'

export type Pending = {
  /** Временный номер: у неотправленного настоящего ещё нет */
  id: string
  chatId: string
  body: string
  replyToId?: string
  createdAt: string
  /** Сколько раз пробовали: после нескольких неудач перестаём спешить */
  tries: number
  /** Последняя попытка не удалась — показываем это человеку */
  failed: boolean
}

function read(): Pending[] {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as Pending[]) : []
  } catch {
    return []
  }
}

function write(items: Pending[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items))
  } catch {
    // Приватный режим и переполненное хранилище: очередь останется
    // только в памяти этой вкладки — это лучше, чем упасть
  }
}

export const outbox = {
  all: (): Pending[] => read(),

  forChat: (chatId: string): Pending[] => read().filter((item) => item.chatId === chatId),

  add: (item: Omit<Pending, 'tries' | 'failed'>): Pending => {
    const pending: Pending = { ...item, tries: 0, failed: false }
    write([...read(), pending])
    return pending
  },

  remove: (id: string): void => {
    write(read().filter((item) => item.id !== id))
  },

  mark: (id: string, changes: Partial<Pending>): void => {
    write(read().map((item) => (item.id === id ? { ...item, ...changes } : item)))
  },

  clearChat: (chatId: string): void => {
    write(read().filter((item) => item.chatId !== chatId))
  },
}

/**
 * Неотправленное показывается в ленте как обычное сообщение — только
 * с часиками вместо галочек. Иначе человек не понимает, отправилось
 * оно или он его вообще не написал.
 */
export function asMessage(pending: Pending, me: { id: string; displayName: string } | null): Message {
  return {
    id: pending.id,
    chatId: pending.chatId,
    body: pending.body,
    isSystem: false,
    deleted: false,
    author: me ? { id: me.id, displayName: me.displayName, avatarUrl: null } : null,
    mine: true,
    forwardedFrom: null,
    replyTo: null,
    cards: [],
    files: [],
    poll: null,
    reactions: [],
    createdAt: pending.createdAt,
    editedAt: null,
    pending: true,
    failed: pending.failed,
  }
}
